import React from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { DownloadIcon, MoreIcon, TrashIcon } from "@/components/ui/icon";
import { ModalCloseButton } from "@/components/common/modal-close-button";
import { useDownload } from "@/hooks/use-download";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";
import { useMomentsTime } from "@/hooks/use-moments-time";
import { Image } from "../common/image";
import { AgentShareModal } from "../agent-share-modal";
import { ShareIcon } from "../agent-share-modal/assets";
import { PublishIcon } from "@/components/agent-share-modal/assets";
import { TextButton } from "@/components/common/text-button";
import { IconButton } from "@/components/common/icon-button";
import { MoreActionItem, MoreActionPopover } from "@/components/common/more-action-popover";
import { Edit3, RefreshCcw } from "lucide-react";
import { ConfirmModal } from "@/components/common/confirm-modal";
import { AccountCard } from "../common/account-card";
import { copyHtml } from "@/utils/copy-html";
import { CopyClipboard } from "../common/copy-clipboard";

export interface AgentAtricleModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  quote?: string;
  contentHtml: string;
  avatarUrl: string;
  nickname: string;
  publishedAt: Date | string | number;
  imageUrl?: string;
  deleteLoading?: boolean;
  isAutoPush?: boolean;
  onCopyQuote?: (quote: string) => void;
  onDownloadImage?: (imageUrl?: string) => void;
  onShare?: () => void;
  onReedit?: () => void;
  onRegenerate?: () => void;
  onDelete?: () => Promise<void>;
}

const CopySvg = ({ className = "w-5 h-5" }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    className={className}
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
  </svg>
);

const ShareSvg = ({ className = "w-5 h-5" }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    className={className}
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <circle cx="18" cy="5" r="3"></circle>
    <circle cx="6" cy="12" r="3"></circle>
    <circle cx="18" cy="19" r="3"></circle>
    <path d="M8.59 13.51l6.83 3.98M15.41 6.51L8.59 10.49"></path>
  </svg>
);

export const AgentAtricleModal: React.FC<AgentAtricleModalProps> = ({
  open,
  onOpenChange,
  title,
  quote,
  contentHtml,
  avatarUrl,
  nickname,
  publishedAt,
  imageUrl,
  deleteLoading,
  isAutoPush,
  onReedit,
  onRegenerate,
  onDelete,
}) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { formatMomentsTime } = useMomentsTime();
  const copyQuote = async () => {
    try {
      await navigator.clipboard.writeText(quote ?? '');
      toast.default({ description: t('agent_creation.agent_modal.copied') });
    } catch {
      toast.default({ description: t('agent_creation.agent_modal.copy_fail') });
    }
  };

  const copyContent = async () => {
    try {
      await copyHtml(contentHtml);
      toast.default({ description: t('agent_creation.agent_modal.copied') });
      return true;
    } catch {
      toast.default({ description: t('agent_creation.agent_modal.copy_fail') });
      return false;
    }
  };

  const { download } = useDownload();
  const downloadImage = async () => {
    try {
      if (!imageUrl) return;
      await download(imageUrl, "article-cover.jpg");
      toast.default({ description: t('agent_creation.agent_modal.download_complete') });
    } catch {
      toast.error({ description: t('agent_creation.agent_modal.download_fail') });
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60" />
        <Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 overflow-hidden h-[90%] aspect-[3/4] rounded-[24px] shadow-lg z-50 bg-white flex flex-col">
          <div className="flex-1 flex flex-col p-6">
            <ModalCloseButton
              className="ml-auto mb-5"
              onClick={() => onOpenChange(false)}
            />
            {/* 标题 */}
            <div className="text-[20px] leading-7 font-medium text-[#111827] mb-4">
              {title}
            </div>

            {/* 摘要块 */}
            <div className="rounded-xl border border-[#e5e7eb] bg-[#f9fafb] p-4 mb-4">
              <div className="flex flex-col items-start justify-between gap-3">
                <div className="flex justify-between flex-1 w-full">
                  <div className="text-xs text-[#6b7280] mb-1">{t('agent_creation.agent_modal.abstract')}</div>
                  <button
                    onClick={copyQuote}
                    className="flex items-center justify-center w-5 rounded text-[#111827]"
                    aria-label={t('agent_creation.agent_modal.copy_abstract')}
                  >
                    <CopySvg className="w-4 h-4" />
                  </button>
                </div>
                <div className="text-sm text-[#111827]">{quote}</div>
              </div>
            </div>

            {/* 作者昵称与时间 */}
            <div className="flex items-center gap-2 mb-4">
              <AccountCard
                compact
                className="size-6"
                name={nickname}
                avatar={avatarUrl}
              />
              <div className="text-sm text-[#111827] font-medium">
                {nickname}
              </div>
              <div className="text-sm text-[#6b7280]">
                {formatMomentsTime(typeof publishedAt === 'number' ? publishedAt * 1000 : publishedAt)}
              </div>
            </div>

            <div className="flex-1 min-h-0">
              <iframe
                title="article-content"
                className="w-full h-full bg-white"
                sandbox=""
                srcDoc={contentHtml}
              />
            </div>
          </div>
          <div className="bg-white py-5 pt-0 px-6 flex justify-end gap-4">
            <MoreActionPopover
              className="w-40"
              side="top"
              align="center"
              tiggerClassName="size-9 rounded-[8px]"
              tiggerIcon={
                <IconButton
                  icon={<MoreIcon className="size-5 !text-[rgba(51,51,51,1)]" />}
                />
              }
            >
              {!isAutoPush && (
                <>
                  <MoreActionItem
                    icon={<Edit3 className="w-4 h-4 text-[rgba(51,51,51,1)]" />}
                    className='text-[rgba(16,24,40,1)]'
                    label={t("agent_creation.content.reedit")}
                    onClick={() => {
                      onReedit?.();
                      onOpenChange(false);
                    }}
                  />
                  {/* <MoreActionItem
                    icon={<RefreshCcw className="w-4 h-4 text-[rgba(51,51,51,1)]" />}
                    className='text-[rgba(16,24,40,1)]'
                    label={t("agent_creation.content.regenerate_again")}
                    onClick={() => {
                      onRegenerate?.();
                      onOpenChange(false);
                    }}
                  /> */}
                </>
              )}
              <ConfirmModal
                trigger={
                  <MoreActionItem
                    icon={<TrashIcon className="w-4 h-4 text-[rgba(51,51,51,1)]" />}
                    className='text-[rgba(16,24,40,1)]'
                    label={t("common.delete")}
                  />
                }
                title={t('agent_creation.content.delete_title')}
                description={t('agent_creation.content.delete_desc')}
                cancel={t('common.cancel')}
                confirm={t('common.delete')}
                onConfirm={async () => {
                  await onDelete?.();
                  onOpenChange(false);
                }}
                loading={deleteLoading}
              />
            </MoreActionPopover>
            <CopyClipboard
              className="size-9 rounded-[8px]"
              iconClassName="w-5 h-5 text-[rgba(51,51,51,1)]"
              onCopy={copyContent}
              icon={<CopySvg className="w-5 h-5 text-[rgba(51,51,51,1)]" />}
            />
            <IconButton
              className="size-9 rounded-[8px]"
              icon={<DownloadIcon className="w-5 h-5 text-[rgba(51,51,51,1)]" />}
              onClick={downloadImage}
              disabled={!imageUrl}
            />
            <AgentShareModal
              showWeChatOfficialAccount
              trigger={
                <TextButton
                  className="px-[22px] py-[10px] rounded-[8px] max-w-max text-sm"
                  icon={<PublishIcon className="w-5 h-5" />}
                  label={t('agent_creation.agent_modal.publish')}
                />
              }
            />
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default AgentAtricleModal;
